import {Conditions} from '@/model/helpers/conditions';
import {and, not, or} from '@/model/helpers/conditions/utils';
import {ReferencesHelpers} from '@/model/helpers/references';
import {isPacDollarSplit, isPrefilledTransaction} from '@/model/helpers/section-comp';
import {buildAmountOrPercentageTemplateValue} from '@/model/helpers/template';
import {Validations} from '@/model/helpers/validations';
import {AllocationMethod, DV_102} from '@/model/src/values/dv-102';
import {Condition, Field, LocalizedString, Reference, SupportsSwitch, SwitchCases} from '@/model/types';
import {clearJArrays} from './clear-j-arrays';
import {isInternallyFundedDepositOrNewContract, isModifyPeriodic} from './operations';
import {useInvestmentFromPreviousSection} from './visibility';

export const ALLOCATION_METHOD_FIELD = 'J47';

export const ALLOCATION_METHOD_FIELDS = ['J47_Periodic', 'J47_Cash', 'J47_EFT', 'J47_Transfer'] as const;

export const PERCENTAGES_MUST_TOTAL_100 = {
  fr: 'La valeur totale des pourcentages doit équivaloir à 100%',
  en: 'The percentages must add up to 100%',
} as const satisfies LocalizedString;

export const isAllocationMethod = {
  percentage: (): Condition => Conditions.fields.eq(ALLOCATION_METHOD_FIELD, AllocationMethod.PERCENTAGE),
  amount: (): Condition => Conditions.fields.eq(ALLOCATION_METHOD_FIELD, AllocationMethod.AMOUNT),
};

export const hasCompletedPeriodicInvestment = or(
  and(isAllocationMethod.percentage(), Conditions.fields.gte('J_Periodic_investment', 100)),
  and(isAllocationMethod.amount(), Conditions.fields.eq('J_Periodic_investment', {$ref: 'J_Periodic_deposit'})),
) satisfies Condition;

export const hasCompletedCashInvestment = or(
  and(isAllocationMethod.percentage(), Conditions.fields.gte('J_Cash_investment', 100)),
  and(isAllocationMethod.amount(), Conditions.fields.eq('J_Cash_investment', {$ref: 'J_Cash_deposit'})),
) satisfies Condition;

export const hasCompletedEFTInvestment = Conditions.fields.gte('J_EFT_investment', 100);

export const hasCompletedTransferInvestment = Conditions.fields.gte('J_Transfer_investment', 100);

// The canonical J47 is only recomputed on the next rebuild, so actions must read the stored radios
const isSelectedAllocationMethodIn = (method: AllocationMethod): Condition =>
  or(...ALLOCATION_METHOD_FIELDS.map(field => Conditions.fields.eq(field, method)));

export const isSelectedAllocationMethod = {
  percentage: (): Condition => isSelectedAllocationMethodIn(AllocationMethod.PERCENTAGE),
  amount: (): Condition => isSelectedAllocationMethodIn(AllocationMethod.AMOUNT),
};

export const j47PeriodicDisplayIf = [
  or(
    and(isModifyPeriodic, or(isPrefilledTransaction, Conditions.fields.gt('T10', 0))),
    isInternallyFundedDepositOrNewContract,
  ),
] as const satisfies Condition[];

export const j47CashDisplayIf = [isInternallyFundedDepositOrNewContract] as const satisfies Condition[];

type AllocationMethodFieldOptions = {
  displayIf?: Condition[];
  readonlyIf?: Condition[];
  initialValue?: SupportsSwitch<Reference | string>;
  valueCases?: SwitchCases<Reference | string>;
};

export const allocationMethodField = (
  id: (typeof ALLOCATION_METHOD_FIELDS)[number],
  {displayIf, readonlyIf, initialValue, valueCases = []}: AllocationMethodFieldOptions = {},
) =>
  ({
    id,
    label: {
      fr: 'Mode de répartition',
      en: 'Allocation method',
    },
    type: 'radio',
    required: true,
    options: ReferencesHelpers.list.all(DV_102),
    initialValue: initialValue ?? {$ref: AllocationMethod.PERCENTAGE},
    value: {
      $switch: [{$case: [useInvestmentFromPreviousSection], $then: {$ref: AllocationMethod.PERCENTAGE}}, ...valueCases],
    },
    onChange: [clearJArrays],
    ...(displayIf ? {displayIf} : {}),
    ...(readonlyIf ? {readonlyIf} : {}),
  }) as const satisfies Field;

export const hiddenAllocationMethodField = (id: (typeof ALLOCATION_METHOD_FIELDS)[number]) =>
  ({
    id,
    label: {
      fr: 'Mode de répartition',
      en: 'Allocation method',
    },
    type: 'hidden',
    value: {$ref: AllocationMethod.PERCENTAGE},
  }) as const satisfies Field;

export const seedAllocationValues = (id: string, source: string) => [
  {
    field: id.asIndex(),
    value: {
      $switch: [
        {$case: [isSelectedAllocationMethod.amount()], $then: {$ref: `${id}_amount`.asIndex()}},
        {$default: {$ref: `${id}_percentage`.asIndex()}},
      ],
    },
  },
  {
    field: `${id}_percentage`.asIndex(),
    value: {
      $switch: [
        {
          $case: [and(isSelectedAllocationMethod.percentage(), not(isPacDollarSplit))],
          $then: {$ref: source.asIndex()},
        },
        {$default: '0'},
      ],
    },
  },
  {
    field: `${id}_amount`.asIndex(),
    value: {
      $switch: [
        {$case: [and(isSelectedAllocationMethod.amount(), isPacDollarSplit)], $then: {$ref: source.asIndex()}},
        {$default: '0'},
      ],
    },
  },
];

export const formatAllocation = (field: string) =>
  buildAmountOrPercentageTemplateValue(field, isAllocationMethod.amount());

type AllocationInputOptions = {
  label: LocalizedString;
  computedLabel: LocalizedString;
  displayIf: Condition[];
  maxSumFields: string[];
  baseValueCases?: SwitchCases<Reference>;
};

export const AMOUNTS_MUST_NOT_EXCEED_DEPOSIT = {
  fr: 'Le montant total ne peut pas dépasser le montant du dépôt',
  en: 'The total amount cannot exceed the deposit amount',
} as const satisfies LocalizedString;

const depositFieldFor = (maxSumFields: string[]): string =>
  maxSumFields.includes('J2#*') ? 'J_Periodic_deposit' : 'J_Cash_deposit';

export const buildAllocationFields = (
  id: string,
  {label, computedLabel, displayIf, maxSumFields, baseValueCases = []}: AllocationInputOptions,
) => {
  const percentageId = `${id}_percentage#{$index}`;
  const amountId = `${id}_amount#{$index}`;

  return [
    {
      id: percentageId,
      label,
      type: 'percentage',
      required: true,
      displayIf: [...displayIf, isAllocationMethod.percentage()],
      validations: [
        Validations.number.gt(0),
        {type: 'maxSum', fields: maxSumFields, value: 100, error: PERCENTAGES_MUST_TOTAL_100},
      ],
    },
    {
      id: amountId,
      label,
      type: 'currency',
      required: true,
      displayIf: [...displayIf, isAllocationMethod.amount()],
      validations: [
        ...Validations.number.validAmount,
        {
          type: 'maxSum',
          fields: maxSumFields,
          value: {$ref: depositFieldFor(maxSumFields)},
          error: AMOUNTS_MUST_NOT_EXCEED_DEPOSIT,
        },
      ],
    },
    {
      id: id.asIndex(),
      label: computedLabel,
      type: 'hidden',
      value: {
        $switch: [
          ...baseValueCases,
          {$case: [Conditions.fields.isFilled(amountId)], $then: {$ref: amountId}},
          {$default: {$ref: percentageId}},
        ],
      },
    },
  ] as const satisfies Field[];
};
